import React, { useState, useEffect } from 'react';
import { MdStar, MdStarHalf, MdStarBorder } from 'react-icons/md';

const StarRating = ({ 
  rating = 0, 
  maxRating = 5, 
  size = 'medium', 
  interactive = false, 
  onRatingChange = null,
  showNumeric = true,
  className = ''
}) => {
  const [hoverRating, setHoverRating] = useState(0);
  const [selectedRating, setSelectedRating] = useState(rating);

  useEffect(() => {
    setSelectedRating(rating);
  }, [rating]);

  const getSizeClasses = () => {
    switch (size) {
      case 'small':
        return 'w-4 h-4';
      case 'large':
        return 'w-8 h-8';
      case 'xlarge':
        return 'w-10 h-10';
      default:
        return 'w-5 h-5';
    }
  };

  const getTextSizeClasses = () => {
    switch (size) {
      case 'small':
        return 'text-sm';
      case 'large':
        return 'text-lg';
      case 'xlarge':
        return 'text-xl';
      default:
        return 'text-base';
    }
  };

  const handleStarClick = (starValue) => {
    if (!interactive) return;
    
    setSelectedRating(starValue);
    if (onRatingChange) {
      onRatingChange(starValue);
    }
  };

  const handleStarHover = (starValue) => {
    if (!interactive) return;
    setHoverRating(starValue);
  };

  const handleMouseLeave = () => {
    if (!interactive) return;
    setHoverRating(0);
  };

  const renderStar = (starIndex) => {
    const starValue = starIndex + 1;
    const currentRating = interactive ? (hoverRating || selectedRating) : selectedRating;
    const difference = currentRating - starIndex;

    let StarIcon;
    let fillClass;
    let isPartialStar = false;

    if (difference >= 1) {
      // Full star
      StarIcon = MdStar;
      fillClass = interactive 
        ? (hoverRating >= starValue ? 'text-yellow-400' : 'text-purple-500')
        : 'text-purple-500';
    } else if (difference > 0) {
      // Partial star (any decimal value > 0)
      isPartialStar = true;
      const percentage = Math.round(difference * 100);
      
      return (
        <div key={starIndex} className={`relative ${getSizeClasses()} ${interactive ? 'cursor-pointer hover:scale-110' : ''}`}
             onClick={() => handleStarClick(starValue)}
             onMouseEnter={() => handleStarHover(starValue)}>
          {/* Background empty star */}
          <MdStarBorder className={`absolute inset-0 ${getSizeClasses()} text-gray-300`} />
          {/* Foreground filled portion */}
          <div className="relative overflow-hidden" style={{ width: `${percentage}%` }}>
            <MdStar className={`${getSizeClasses()} text-purple-500`} />
          </div>
        </div>
      );
    } else {
      // Empty star
      StarIcon = MdStarBorder;
      fillClass = interactive 
        ? (hoverRating >= starValue ? 'text-yellow-400' : 'text-gray-300')
        : 'text-gray-300';
    }

    return (
      <StarIcon
        key={starIndex}
        className={`${getSizeClasses()} ${fillClass} transition-colors duration-200 ${
          interactive ? 'cursor-pointer hover:scale-110' : ''
        }`}
        onClick={() => handleStarClick(starValue)}
        onMouseEnter={() => handleStarHover(starValue)}
      />
    );
  };

  return (
    <div 
      className={`flex items-center gap-1 ${className}`}
      onMouseLeave={handleMouseLeave}
    >
      <div className="flex items-center">
        {Array.from({ length: maxRating }, (_, index) => renderStar(index))}
      </div>
      
      {showNumeric && (
        <span className={`ml-2 font-medium text-gray-700 ${getTextSizeClasses()}`}>
          {(interactive ? selectedRating : selectedRating).toFixed(1)} / {maxRating}
        </span>
      )}
      
      {interactive && (
        <span className={`ml-1 text-gray-500 ${getTextSizeClasses()} hidden sm:inline`}>
          ({hoverRating > 0 ? 'Click to rate' : 'Hover to preview'})
        </span>
      )}
    </div>
  );
};

export default StarRating;